import { Injectable, signal } from "@angular/core";
import type { ResultType } from "../model/block.types";

/** One result block within a tool_data_stream SSE event. */
export interface ToolDataStreamResult {
  block_id: string;
  result_type: ResultType;
  result_data: unknown;
}

/** Full tool_data_stream SSE event payload. */
export interface ToolDataStreamEvent {
  tool_call_id: string;
  tool_name: string;
  args: Record<string, unknown>;
  results: ToolDataStreamResult[];
}

/**
 * Stores tool_data_stream SSE result blocks indexed by block_id.
 * Consumers (ToolDataCardComponent) inject this to retrieve the actual result
 * when the content block arrives with result.value === null.
 */
@Injectable({ providedIn: "root" })
export class ToolDataStreamService {
  // Map wrapped in a signal so computed() consumers react when entries are added
  readonly #store = signal<Map<string, ToolDataStreamResult>>(new Map());

  readonly payloads = this.#store.asReadonly();

  /** Store all result blocks from an event, each keyed by block_id. */
  store(event: ToolDataStreamEvent): void {
    this.#store.update((map) => {
      const next = new Map(map);
      for (const result of event.results) {
        next.set(result.block_id, result);
      }
      return next;
    });
  }

  /** Retrieve a stored result block by block_id. Reads the signal for reactivity. */
  get(blockId: string): ToolDataStreamResult | undefined {
    return this.#store().get(blockId);
  }
}
